import React from "react";
import { TemplateFormData } from "../../../views/marketing-pages/templates";
import Input from "../../input";
import { SectionLabel } from "./utiz";

export const extractVariableIndices = (messageBody: string): number[] => {
  const matches = Array.from(messageBody.matchAll(/{{(\d+)}}/g));
  const indices = Array.from(new Set(matches.map((m) => parseInt(m[1], 10))));
  return indices.sort((a, b) => a - b);
};

// WhatsApp requires a non-empty "example" value for every {{n}} placeholder
// used in the body text before it will accept the template for review.
export const MessageVariablesSection: React.FC<{
  formData: TemplateFormData;
  onChange: (u: Partial<TemplateFormData>) => void;
}> = ({ formData, onChange }) => {
  // Union of what's actually in the text right now AND whatever the
  // template already declares in messageVariables — normally these are the
  // same set (a {{n}} token's declared variable stays in sync for WhatsApp,
  // which never touches the body after creation). But an SMS template saved
  // while the now-reverted literal-substitution behavior was live can have
  // a declared variable (name/index/example from the backend) whose {{n}}
  // token no longer exists in `content` at all — this still needs to show
  // up so the row (and its data) isn't just silently invisible.
  const textIndices = extractVariableIndices(formData.messageBody);
  const knownIndices = (formData.messageVariables || []).map((v) => v.index);
  const indices = Array.from(new Set([...textIndices, ...knownIndices])).sort((a, b) => a - b);

  if (indices.length === 0) return null;

  const updateVariable = (index: number, patch: { name?: string; example?: string }) => {
    const existing = formData.messageVariables || [];
    const found = existing.find((v) => v.index === index);
    const updated = found
      ? existing.map((v) => (v.index === index ? { ...v, ...patch } : v))
      : [...existing, { index, name: `variable_${index}`, example: "", ...patch }];
    onChange({ messageVariables: updated });
  };

  return (
    <div>
      <SectionLabel label="Message Body Variables" />
      <div className="space-y-3">
        {indices.map((index) => {
          const variable = (formData.messageVariables || []).find((v) => v.index === index);
          return (
            <div key={index} className="grid grid-cols-[64px_1fr_1fr] gap-2 items-center">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{`{{${index}}}`}</span>
              <Input
                value={variable?.name || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateVariable(index, { name: e.target.value })}
                placeholder="Variable name, e.g. customer_name"
              />
              <Input
                value={variable?.example || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateVariable(index, { example: e.target.value })}
                placeholder="Example value, e.g. John"
              />
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1.5">
        Add an example value for each variable — WhatsApp needs this to review your template.
      </p>
    </div>
  );
};
